import { useFormikContext } from 'formik';
import React from 'react';

import { FormikCheckbox } from '~commonInfra/components/FormikCheckbox';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { MetadataDrivenSiteSettings } from '~src/commonInfra/components/MetadataDrivenSiteSetting';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';

import { Messages } from '../../../../../codegen/Localize';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { groupNameMessage } from '../../../../styles/CommonStyles';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '../../site-settings/ReactSiteSettingsForSiteAdmin';
import { forceUncheckedIfUserVisibilityLimited, useUserVisibilityLimited } from '../user-visibility/UserVisibilitySection';

export const COMMENTING_ENABLED_FIELD = 'settings.siteCommentingSettings.commentingEnabled';
export const COMMENTING_MENTIONS_ENABLED_FIELD = 'settings.siteCommentingSettings.commentingMentionsEnabled';
export const COMMENTING_SECTION_NAME = 'commenting';
export const COMMENTING_PREFIX = 'settings.siteCommentingSettings';

export type CommentingSettingSectionProps = SiteSettingsForSiteAdminProps< 'siteCommentingSettings' | 'userVisibility'>;

export function CommentingSettingSection(props: CommentingSettingSectionProps) {
  const sessionManagementService = getSessionManagementServiceInstance();
  const formik = useFormikContext<CommentingSettingSectionProps>();
  const isCommentingEnabled = props.settings.siteCommentingSettings?.commentingEnabled;
  const commentSettingLabel = sessionManagementService.isUserNotificationsPlatformEnabled() ? Messages.commentingMentionsSiteSettingGeneral() : Messages.commentingMentionsSiteSetting();
  // isUserVisibilityLimited calls a hook. Save this value as a variable to keep the rules of hooks preserved
  const userVisibilityLimited = useUserVisibilityLimited();

  if (FeatureFlags.metadataDrivenSiteSettingsEnabled) {
      return (
          <SettingsSection
              title={Messages.commentingSiteSettings()}
              testId={'commenting-setting-section'}>
                  <MetadataDrivenSiteSettings
                      sectionName={COMMENTING_SECTION_NAME}
                      formikPrefix={COMMENTING_PREFIX}/>
          </SettingsSection>
      );
  }

  return <SettingsSection title={renderTitle(userVisibilityLimited)}
                          testId={'commenting-setting-section'}
                          description={Messages.commentingSiteSettingsDetails()}
                          disabled={userVisibilityLimited}>
            <FormikCheckbox disabled={userVisibilityLimited}
                name={COMMENTING_ENABLED_FIELD}
                label={Messages.commentingSiteSettingsEnable()}
                testId={'commenting-enabled'}
                {...forceUncheckedIfUserVisibilityLimited(userVisibilityLimited)}>
            </FormikCheckbox>
            {FeatureFlags.commentingMentionsEnabled && isCommentingEnabled &&
              <FormikCheckbox name={COMMENTING_MENTIONS_ENABLED_FIELD}
                              label={commentSettingLabel}
                              testId={'commenting-mentions-enabled'}
                              disabled={userVisibilityLimited}
                              {...forceUncheckedIfUserVisibilityLimited(userVisibilityLimited)}
              />
            }
  </SettingsSection>;
}

function renderTitle(isUserVisibilityLimited: boolean) {
  return <>
    <div>{Messages.commentingSiteSettings()}</div>
    {isUserVisibilityLimited &&
      <span className={groupNameMessage} data-tb-test-id={'commenting-uservisibility-warning'}>
        {Messages.userVisibilityWarning()}
    </span>
    }
  </>;
}

export const CommentingSettingSectionSpecs = componentRegistryEntry({
  name: 'CommentingSettingSection',
  component: CommentingSettingSection
});
